import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import { useAuth } from '../../contexts/AuthContext';
import type { BiltEvent, EventClass, EventRegistration, UpdateEventPayload } from '../../types/event';
import type {
  ClassProgress,
  EventJudge,
  EventResult,
  VehicleAward,
} from '../../types/judging';
import { isAdminEditingOthersEvent } from '../../utils/permissions';
import PageLoader from '../ui/PageLoader';
import Button from '../ui/Button';
import PageBackLink from '../ui/PageBackLink';
import AdminEditingBanner from '../ui/AdminEditingBanner';
import JudgeUserCombobox, { type JudgeUserOption } from './JudgeUserCombobox';
import EventBadgesPanel from './EventBadgesPanel';
import { EntityPhotoUpload } from '../EntityPhotoUpload';
import CommunityPostFeed from '../community/CommunityPostFeed';
import EventReviewQueue from './EventReviewQueue';
import EventRosterSummary from './EventRosterSummary';
import EventSpectatorTicketsPanel from './EventSpectatorTicketsPanel';
import EventPricingPanel from './EventPricingPanel';
import EventDayOpsBar from './EventDayOpsBar';
import type { EntryMode, PaidFeatureFlags, RegistrationReviewData } from '../../types/event';
import { entryModeDescription, entryModeLabel, isPaidEntryMode } from '../../utils/registrationStatus';

type Tab = 'updates' | 'review' | 'classes' | 'registrations' | 'pricing' | 'tickets' | 'judges' | 'results';

const TAB_META: Array<{ id: Tab; label: string; group: string }> = [
  { id: 'updates', label: 'Updates', group: 'Setup' },
  { id: 'classes', label: 'Classes', group: 'Setup' },
  { id: 'pricing', label: 'Pricing', group: 'Money' },
  { id: 'tickets', label: 'Tickets', group: 'Money' },
  { id: 'review', label: 'Review', group: 'Roster' },
  { id: 'registrations', label: 'Roster', group: 'Roster' },
  { id: 'judges', label: 'Judges', group: 'Judging' },
  { id: 'results', label: 'Results', group: 'Judging' },
];

const PLACEMENTS = ['1st Place', '2nd Place', '3rd Place'] as const;

const EventAdmin: React.FC = () => {
  const { eventId: eventIdParam } = useParams<{ eventId: string }>();
  const eventId = Number(eventIdParam);
  const { user } = useAuth();

  const [event, setEvent] = useState<BiltEvent | null>(null);
  const [classes, setClasses] = useState<EventClass[]>([]);
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [tab, setTab] = useState<Tab>('updates');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newClassName, setNewClassName] = useState('');
  const [newClassDesc, setNewClassDesc] = useState('');
  const [classBusy, setClassBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [judges, setJudges] = useState<EventJudge[]>([]);
  const [results, setResults] = useState<EventResult[]>([]);
  const [classProgress, setClassProgress] = useState<ClassProgress[]>([]);
  const [judgeAssignedCount, setJudgeAssignedCount] = useState(0);
  const [resultsFormula, setResultsFormula] = useState('');
  const [awards, setAwards] = useState<VehicleAward[]>([]);
  const [selectedJudge, setSelectedJudge] = useState<JudgeUserOption | null>(null);
  const [judgeClassIds, setJudgeClassIds] = useState<number[]>([]);
  const [judgeBusy, setJudgeBusy] = useState(false);
  const [publishBusy, setPublishBusy] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [editingJudgeClasses, setEditingJudgeClasses] = useState<number | null>(null);
  const [detailsBusy, setDetailsBusy] = useState(false);
  const [detailsSaved, setDetailsSaved] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editState, setEditState] = useState('');
  const [editStartAt, setEditStartAt] = useState('');
  const [editEndAt, setEditEndAt] = useState('');
  const [reviewData, setReviewData] = useState<RegistrationReviewData | null>(null);
  const [paidFlags, setPaidFlags] = useState<PaidFeatureFlags>({});
  const [entryModeBusy, setEntryModeBusy] = useState(false);

  const toLocalInput = (value: string | null): string => {
    if (!value) return '';
    const normalized = value.includes('T') ? value : value.replace(' ', 'T');
    return normalized.slice(0, 16);
  };

  const syncDetailsForm = useCallback((evt: BiltEvent) => {
    setEditTitle(evt.title || '');
    setEditDescription(evt.description || '');
    setEditLocation(evt.location || '');
    setEditCity(evt.city || '');
    setEditState(evt.state || '');
    setEditStartAt(toLocalInput(evt.start_at));
    setEditEndAt(toLocalInput(evt.end_at));
  }, []);

  const load = useCallback(async (opts?: { soft?: boolean }) => {
    if (!eventId || isNaN(eventId)) return;
    try {
      // Soft refresh (tab actions / review changes) must not unmount the whole admin page.
      if (!opts?.soft) setLoading(true);
      setError(null);
      const [evt, cls, regs, jdg, res, awd] = await Promise.all([
        apiService.getEvent(eventId),
        apiService.getEventClasses(eventId),
        apiService.getEventRegistrations(eventId),
        apiService.getEventJudges(eventId),
        apiService.getEventResults(eventId),
        apiService.getEventAwards(eventId),
      ]);
      setEvent(evt);
      syncDetailsForm(evt);
      setClasses(cls);
      setRegistrations(regs);
      setJudges(jdg);
      setResults(res.results);
      setClassProgress(res.class_progress);
      setJudgeAssignedCount(res.judge_assigned_count);
      setResultsFormula(res.formula || '');
      setAwards(awd);
      try {
        const flags = await apiService.getPaidFeatureFlags();
        setPaidFlags(flags);
      } catch {
        /* flags optional */
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load event');
    } finally {
      if (!opts?.soft) setLoading(false);
    }
  }, [eventId, syncDetailsForm]);

  const loadReview = useCallback(async () => {
    if (!eventId || isNaN(eventId)) return;
    try {
      setReviewData(await apiService.getRegistrationReview(eventId));
    } catch (err: any) {
      setError(err.message);
    }
  }, [eventId]);

  useEffect(() => {
    if (tab === 'review') void loadReview();
  }, [tab, loadReview]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleAddClass = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newClassName.trim()) return;
    try {
      setClassBusy(true);
      await apiService.createEventClass({
        event_id: eventId,
        name: newClassName.trim(),
        description: newClassDesc.trim() || undefined,
      });
      setNewClassName('');
      setNewClassDesc('');
      const cls = await apiService.getEventClasses(eventId);
      setClasses(cls);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setClassBusy(false);
    }
  };

  const handleDeleteClass = async (id: number) => {
    if (!window.confirm('Delete this class?')) return;
    try {
      setClassBusy(true);
      await apiService.deleteEventClass(id);
      setClasses((prev) => prev.filter((c) => c.id !== id));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setClassBusy(false);
    }
  };

  const moveClass = async (index: number, direction: -1 | 1) => {
    const next = index + direction;
    if (next < 0 || next >= classes.length) return;
    const reordered = [...classes];
    [reordered[index], reordered[next]] = [reordered[next], reordered[index]];
    try {
      setClassBusy(true);
      const updated = await apiService.reorderEventClasses(eventId, reordered.map((c) => c.id));
      setClasses(updated);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setClassBusy(false);
    }
  };

  const handleExport = async () => {
    try {
      setExportBusy(true);
      await apiService.exportEventRegistrationsCsv(eventId);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setExportBusy(false);
    }
  };

  const handleAddJudge = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedJudge) {
      setError('Search and select a judge first');
      return;
    }
    try {
      setJudgeBusy(true);
      await apiService.addEventJudge(
        eventId,
        selectedJudge.username,
        judgeClassIds.length > 0 ? judgeClassIds : undefined,
        selectedJudge.id
      );
      setSelectedJudge(null);
      setJudgeClassIds([]);
      setJudges(await apiService.getEventJudges(eventId));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setJudgeBusy(false);
    }
  };

  const handleRemoveJudge = async (id: number) => {
    try {
      setJudgeBusy(true);
      await apiService.removeEventJudge(id);
      setJudges((prev) => prev.filter((j) => j.id !== id));
    } catch (err: any) {
      setError(err.message);
    } finally {
      setJudgeBusy(false);
    }
  };

  const toggleJudgeClassId = (cid: number) => {
    setJudgeClassIds((prev) =>
      prev.includes(cid) ? prev.filter((id) => id !== cid) : [...prev, cid]
    );
  };

  const handleUpdateJudgeClasses = async (assignmentId: number, classIds: number[]) => {
    try {
      setJudgeBusy(true);
      await apiService.updateEventJudgeClasses(assignmentId, classIds);
      setJudges(await apiService.getEventJudges(eventId));
      setEditingJudgeClasses(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setJudgeBusy(false);
    }
  };

  const confirmPublishIfNeeded = (
    result: EventResult
  ): { ok: boolean; forceIncomplete: boolean } => {
    const classId = result.class_id ?? null;
    const progress =
      classId != null ? classProgress.find((c) => c.class_id === classId) : null;
    let forceIncomplete = false;
    if (progress && !progress.ready_for_results) {
      if (
        !window.confirm(
          `"${progress.name}" still has ${progress.remaining} vehicle(s) without complete judging. Publish anyway?`
        )
      ) {
        return { ok: false, forceIncomplete: false };
      }
      forceIncomplete = true;
    }
    if (result.is_tied) {
      if (
        !window.confirm(
          'This result is tied with another vehicle in the class. Confirm placement before publishing?'
        )
      ) {
        return { ok: false, forceIncomplete: false };
      }
    }
    return { ok: true, forceIncomplete };
  };

  const handlePublishTopAward = async (result: EventResult, placement: string) => {
    const confirm = confirmPublishIfNeeded(result);
    if (!confirm.ok) return;
    try {
      setPublishBusy(true);
      const published = await apiService.publishAwards(
        eventId,
        [
          {
            registration_id: result.registration_id,
            title: `${placement}: ${event?.title || 'Event'}`,
            placement,
            class_id: result.class_id ?? undefined,
          },
        ],
        { forceIncomplete: confirm.forceIncomplete }
      );
      setAwards((prev) => [...published, ...prev]);
      void apiService.trackEvent('award_published', {
        event_id: eventId,
        metadata: { registration_id: result.registration_id, placement },
      });
    } catch (err: any) {
      if (err?.code === 'incomplete_class') {
        const retry = window.confirm(
          `${err.message || 'Class scoring is incomplete.'} Publish anyway?`
        );
        if (retry) {
          try {
            const published = await apiService.publishAwards(
              eventId,
              [
                {
                  registration_id: result.registration_id,
                  title: `${placement}: ${event?.title || 'Event'}`,
                  placement,
                  class_id: result.class_id ?? undefined,
                },
              ],
              { forceIncomplete: true }
            );
            setAwards((prev) => [...published, ...prev]);
            return;
          } catch (retryErr: any) {
            setError(retryErr.message);
            return;
          }
        }
      }
      setError(err.message);
    } finally {
      setPublishBusy(false);
    }
  };

  const handleUnlockScores = async (registrationId: number, label: string) => {
    if (
      !window.confirm(
        `Unlock finalized scores for ${label}? Judges will be able to edit again.`
      )
    ) {
      return;
    }
    try {
      setPublishBusy(true);
      await apiService.unlockJudgeScore({ registration_id: registrationId });
      void apiService.trackEvent('judge_score_unlocked', {
        event_id: eventId,
        metadata: { registration_id: registrationId },
      });
      const res = await apiService.getEventResults(eventId);
      setResults(res.results);
      setClassProgress(res.class_progress);
      setJudgeAssignedCount(res.judge_assigned_count);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setPublishBusy(false);
    }
  };

  const resultsByClass = (() => {
    const map = new Map<string, { name: string; classId: number | null; items: EventResult[] }>();
    for (const r of results) {
      const key = r.class_id != null ? String(r.class_id) : 'none';
      if (!map.has(key)) {
        map.set(key, {
          name: r.class_name || 'No class',
          classId: r.class_id ?? null,
          items: [],
        });
      }
      map.get(key)!.items.push(r);
    }
    return Array.from(map.values());
  })();

  const vehicleLabel = (r: EventRegistration) => {
    const v = r.vehicle;
    if (!v) return 'Unknown vehicle';
    return v.nickname || `${v.year} ${v.make} ${v.model}`.trim();
  };

  if (loading) {
    return <PageLoader message="Loading event admin…" />;
  }

  if (error && !event) {
    return (
      <div className="py-12 text-center">
        <p className="text-red-400 mb-4" role="alert">{error}</p>
        <Link to="/organizer" className="btn-primary min-h-[44px] inline-flex">
          Back to Organizer
        </Link>
      </div>
    );
  }

  if (!event) return null;

  const handleEventSettings = async (updates: UpdateEventPayload) => {
    try {
      setSettingsBusy(true);
      setError(null);
      const updated = await apiService.updateEvent(eventId, updates);
      setEvent(updated);
      syncDetailsForm(updated);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSettingsBusy(false);
    }
  };

  const handleSaveDetails = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editTitle.trim()) {
      setError('Title is required');
      return;
    }
    try {
      setDetailsBusy(true);
      setDetailsSaved(false);
      setError(null);
      const payload: UpdateEventPayload = {
        title: editTitle.trim(),
        description: editDescription.trim() || null,
        location: editLocation.trim() || null,
        city: editCity.trim() || null,
        state: editState.trim() || null,
        start_at: editStartAt || null,
        end_at: editEndAt || null,
      };
      const updated = await apiService.updateEvent(eventId, payload);
      setEvent(updated);
      syncDetailsForm(updated);
      setDetailsSaved(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDetailsBusy(false);
    }
  };

  const inputClass = 'input';
  const labelClass = 'input-label';
  const adminEditingOthers = isAdminEditingOthersEvent(user, event);

  return (
    <div className="max-w-5xl">
        <PageBackLink label="All events" to="/organizer" className="mb-4" />

        {adminEditingOthers && (
          <AdminEditingBanner
            contentLabel="event"
            ownerLabel={
              event.organizer_username
                ? `@${event.organizer_username}`
                : `organizer #${event.organizer_id}`
            }
            className="mb-4"
          />
        )}

        <div className="mb-6">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Event management</p>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="heading-display tracking-tight">{event.title}</h1>
              <p className="text-muted text-sm mt-2 font-mono">/events/{event.slug}</p>
            </div>
            <span className="inline-flex items-center text-xs px-2.5 py-1 rounded-md border border-white/[0.08] bg-[#141414] text-gray-300 capitalize">
              Status: {event.status}
            </span>
          </div>
        </div>

        <EventDayOpsBar eventId={eventId} slug={event.slug} status={event.status} />

        {error && (
          <div className="bg-red-950/30 border border-red-900/50 text-red-300 rounded-xl px-4 py-3 mb-4 text-sm">
            {error}
          </div>
        )}

        <section className="form-section mb-6">
          <EntityPhotoUpload
            label="Event flyer"
            helpText="Main image for the public event page and listings. JPEG, PNG, or WebP up to 10MB."
            shape="banner"
            maxSizeMb={10}
            currentUrl={event.flyer_path}
            onUpload={async (file) => {
              const result = await apiService.uploadEventFlyer(eventId, file);
              return result.flyer_path;
            }}
            onDelete={async () => {
              await apiService.deleteEventFlyer(eventId);
            }}
            onUpdated={(url) => {
              setEvent((prev) => (prev ? { ...prev, flyer_path: url } : prev));
            }}
            onError={(msg) => setError(msg)}
          />
        </section>

        <form
          onSubmit={handleSaveDetails}
          className="form-section mb-6"
        >
          <div>
            <h2 className="heading-section text-sm">Event details</h2>
            <p className="text-xs text-gray-500 mt-1">
              Update title, description, location, and dates anytime, including after the event.
            </p>
          </div>
          <div>
            <label htmlFor="edit-event-title" className={labelClass}>
              Title <span className="text-red-400">*</span>
            </label>
            <input
              id="edit-event-title"
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className={inputClass}
              maxLength={150}
              required
            />
          </div>
          <div>
            <label htmlFor="edit-event-description" className={labelClass}>Description</label>
            <textarea
              id="edit-event-description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              className={inputClass}
              rows={3}
            />
          </div>
          <div>
            <label htmlFor="edit-event-location" className={labelClass}>Venue / location</label>
            <input
              id="edit-event-location"
              type="text"
              value={editLocation}
              onChange={(e) => setEditLocation(e.target.value)}
              className={inputClass}
              maxLength={255}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="edit-event-city" className={labelClass}>City</label>
              <input
                id="edit-event-city"
                type="text"
                value={editCity}
                onChange={(e) => setEditCity(e.target.value)}
                className={inputClass}
                maxLength={100}
              />
            </div>
            <div>
              <label htmlFor="edit-event-state" className={labelClass}>State / region</label>
              <input
                id="edit-event-state"
                type="text"
                value={editState}
                onChange={(e) => setEditState(e.target.value)}
                className={inputClass}
                maxLength={100}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="edit-event-start" className={labelClass}>Starts</label>
              <input
                id="edit-event-start"
                type="datetime-local"
                value={editStartAt}
                onChange={(e) => setEditStartAt(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="edit-event-end" className={labelClass}>Ends</label>
              <input
                id="edit-event-end"
                type="datetime-local"
                value={editEndAt}
                onChange={(e) => setEditEndAt(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" variant="primary" size="sm" loading={detailsBusy}>
              Save details
            </Button>
            {detailsSaved && <span className="text-xs text-emerald-400">Saved</span>}
          </div>
        </form>

        <div className="form-section mb-6">
          <div>
            <h2 className="font-semibold text-sm text-white">Visibility</h2>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
              Builders can register when status is <strong className="text-gray-400">Published</strong> and the event is public.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label htmlFor="evt-status" className="input-label">Status</label>
              <select
                id="evt-status"
                value={event.status}
                disabled={settingsBusy}
                onChange={(e) => handleEventSettings({ status: e.target.value as BiltEvent['status'] })}
                className={inputClass}
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-300 pb-2">
              <input
                type="checkbox"
                checked={event.is_public}
                disabled={settingsBusy}
                onChange={(e) => handleEventSettings({ is_public: e.target.checked })}
                className="checkbox-control"
              />
              Publicly viewable
            </label>
            <label className="block text-sm text-gray-300 pb-2 w-full sm:max-w-xs">
              Entry mode
              <select
                className="input mt-1"
                value={event.entry_mode || 'open_free'}
                disabled={settingsBusy || entryModeBusy}
                onChange={async (e) => {
                  const next = e.target.value as EntryMode;
                  if (isPaidEntryMode(next) && !paidFlags.paid_vehicle_entry_enabled) return;
                  try {
                    setEntryModeBusy(true);
                    await handleEventSettings({ entry_mode: next });
                  } finally {
                    setEntryModeBusy(false);
                  }
                }}
              >
                <option value="open_free">{entryModeLabel('open_free')}</option>
                <option value="pre_approval_free">{entryModeLabel('pre_approval_free')}</option>
                {paidFlags.paid_vehicle_entry_enabled ? (
                  <>
                    <option value="pre_approval_paid">{entryModeLabel('pre_approval_paid')}</option>
                    <option value="open_paid">{entryModeLabel('open_paid')}</option>
                  </>
                ) : null}
              </select>
              <span className="block text-xs text-gray-600 mt-1">{entryModeDescription(event.entry_mode)}</span>
            </label>
          </div>
        </div>

        <div className="mb-6" role="tablist" aria-label="Event sections">
          <div className="flex gap-1 border-b border-white/[0.06] overflow-x-auto">
            {TAB_META.map((t) => {
              const selected = tab === t.id;
              const count =
                t.id === 'review' && reviewData
                  ? ` (${reviewData.counts.pending_approval})`
                  : t.id === 'registrations'
                    ? ` (${registrations.length})`
                    : t.id === 'classes'
                      ? ` (${classes.length})`
                      : t.id === 'judges'
                        ? ` (${judges.length})`
                        : '';
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setTab(t.id)}
                  className={`px-3 sm:px-4 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap min-h-[44px] cursor-pointer transition-colors duration-200 ${
                    selected
                      ? 'border-orange-400 text-white'
                      : 'border-transparent text-gray-500 hover:text-gray-300'
                  }`}
                >
                  <span className="block text-[10px] uppercase tracking-wider text-gray-600 mb-0.5">
                    {t.group}
                  </span>
                  {t.label}
                  {count}
                </button>
              );
            })}
          </div>
        </div>

        {tab === 'updates' && event ? (
          <CommunityPostFeed
            scope="event"
            entityId={event.id}
            canView
            canPost
            emptyMemberHint="Post schedule changes, gate notes, and logistics. Registered entrants get notified."
          />
        ) : null}

        {tab === 'classes' && (
          <div className="space-y-6">
            <form onSubmit={handleAddClass} className="form-section space-y-3">
            <h2 className="heading-section text-sm">Add class</h2>
              <input
                type="text"
                value={newClassName}
                onChange={(e) => setNewClassName(e.target.value)}
                placeholder="Class name"
                className={inputClass}
                maxLength={120}
                required
              />
              <input
                type="text"
                value={newClassDesc}
                onChange={(e) => setNewClassDesc(e.target.value)}
                placeholder="Description (optional)"
                className={inputClass}
                maxLength={255}
              />
              <Button type="submit" variant="primary" size="sm" loading={classBusy} disabled={!newClassName.trim()}>
                Add class
              </Button>
            </form>

            {classes.length === 0 ? (
              <p className="text-gray-500 text-sm">Add at least one class before builders can register.</p>
            ) : (
              <ul className="space-y-2">
                {classes.map((cls, index) => (
                  <li key={cls.id} className="flex items-center gap-2 card px-4 py-3.5">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{cls.name}</p>
                      {cls.description && <p className="text-xs text-gray-400 truncate">{cls.description}</p>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button type="button" disabled={classBusy || index === 0} onClick={() => moveClass(index, -1)} className="p-1 text-gray-400 hover:text-white disabled:opacity-30" aria-label="Move up">↑</button>
                      <button type="button" disabled={classBusy || index === classes.length - 1} onClick={() => moveClass(index, 1)} className="p-1 text-gray-400 hover:text-white disabled:opacity-30" aria-label="Move down">↓</button>
                      <button type="button" disabled={classBusy} onClick={() => handleDeleteClass(cls.id)} className="p-1 text-red-400 hover:text-red-300 text-sm ml-2">Delete</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === 'review' && (
          <div className="space-y-6">
            {reviewData ? (
              <EventRosterSummary counts={reviewData.counts} capacity={event.vehicle_capacity} />
            ) : null}
            <EventReviewQueue
              eventId={eventId}
              classes={classes}
              refundsEnabled={Boolean(paidFlags.payment_refunds_enabled)}
              onChanged={() => {
                void loadReview();
                void load({ soft: true });
              }}
            />
          </div>
        )}

        {tab === 'pricing' && event ? (
          <EventPricingPanel
            eventId={eventId}
            entryMode={event.entry_mode}
            paidEntryFlagEnabled={Boolean(paidFlags.paid_vehicle_entry_enabled)}
          />
        ) : null}

        {tab === 'tickets' && event ? (
          <EventSpectatorTicketsPanel
            eventId={eventId}
            eventSlug={event.slug}
            ticketsEnabled={Boolean(paidFlags.spectator_tickets_enabled)}
          />
        ) : null}

        {tab === 'registrations' && (
          <div>
            <div className="flex justify-end mb-4">
              <Button variant="secondary" size="sm" loading={exportBusy} onClick={handleExport} disabled={registrations.length === 0} className="min-h-[44px]">
                Export CSV
              </Button>
            </div>
            {registrations.length === 0 ? (
              <p className="text-gray-500 text-sm">No registrations yet. Share your public event page to open registration.</p>
            ) : (
              <>
                <ul className="space-y-3 md:hidden">
                  {registrations.map((r) => (
                    <li key={r.id} className="card p-4">
                      <p className="font-medium text-white">{vehicleLabel(r)}</p>
                      <p className="text-sm text-gray-500 mt-1">@{r.owner?.username ?? '-'}</p>
                      <div className="flex flex-wrap gap-2 mt-2 text-xs text-gray-600">
                        {r.class?.name && <span>{r.class.name}</span>}
                        <span className="capitalize">{r.status}</span>
                        {r.created_at && (
                          <span>{new Date(r.created_at.replace(' ', 'T')).toLocaleDateString()}</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="text-gray-500 border-b border-white/[0.06]">
                        <th className="py-2 pr-4 font-medium">Vehicle</th>
                        <th className="py-2 pr-4 font-medium">Owner</th>
                        <th className="py-2 pr-4 font-medium">Class</th>
                        <th className="py-2 pr-4 font-medium">Status</th>
                        <th className="py-2 font-medium">Registered</th>
                      </tr>
                    </thead>
                    <tbody>
                      {registrations.map((r) => (
                        <tr key={r.id} className="border-b border-white/[0.06]">
                          <td className="py-3 pr-4">{vehicleLabel(r)}</td>
                          <td className="py-3 pr-4">{r.owner?.username ?? '-'}</td>
                          <td className="py-3 pr-4">{r.class?.name ?? '-'}</td>
                          <td className="py-3 pr-4 capitalize">{r.status}</td>
                          <td className="py-3 text-gray-500">
                            {r.created_at ? new Date(r.created_at.replace(' ', 'T')).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'judges' && (
          <div className="space-y-6">
            <form onSubmit={handleAddJudge} className="form-section space-y-3">
              <h2 className="font-semibold text-sm text-white">Add a judge</h2>
              <JudgeUserCombobox
                eventId={eventId}
                selected={selectedJudge}
                onSelect={setSelectedJudge}
                disabled={judgeBusy}
                inputClassName={inputClass}
              />
              {classes.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">
                    Restrict to classes (optional; leave empty for all classes)
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {classes.map((c) => (
                      <label
                        key={c.id}
                        className={judgeClassIds.includes(c.id) ? 'filter-chip-active gap-2' : 'filter-chip-inactive gap-2'}
                      >
                        <input
                          type="checkbox"
                          checked={judgeClassIds.includes(c.id)}
                          onChange={() => toggleJudgeClassId(c.id)}
                          className="checkbox-control"
                        />
                        {c.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={judgeBusy}
                disabled={!selectedJudge}
              >
                Add to event
              </Button>
            </form>
            {judges.length === 0 ? (
              <p className="text-gray-500 text-sm">
                No judges assigned. Search for a registered Bilt user by name or username.
              </p>
            ) : (
              <ul className="space-y-3">
                {judges.map((j) => {
                  const classNames =
                    j.classes && j.classes.length > 0
                      ? j.classes.map((c) => c.name || `Class ${c.id}`).join(', ')
                      : 'All classes';
                  const prog = j.progress;
                  return (
                    <li
                      key={j.id}
                      className="card px-4 py-3.5 space-y-2"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium">@{j.username}</p>
                          <p className="text-xs text-gray-500 mt-1">Classes: {classNames}</p>
                          {prog && (
                            <p className="text-xs text-gray-500 mt-1">
                              Progress: {prog.finalized} finalized · {prog.drafts} draft
                              {prog.drafts === 1 ? '' : 's'}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={() =>
                              setEditingJudgeClasses(editingJudgeClasses === j.id ? null : j.id)
                            }
                            className="text-gray-400 text-sm hover:text-white min-h-[40px] px-2"
                          >
                            Classes
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveJudge(j.id)}
                            className="text-red-400 text-sm min-h-[40px] px-2"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      {editingJudgeClasses === j.id && (
                        <JudgeClassEditor
                          classes={classes}
                          initialIds={j.class_ids || []}
                          busy={judgeBusy}
                          onSave={(ids) => handleUpdateJudgeClasses(j.id, ids)}
                          onCancel={() => setEditingJudgeClasses(null)}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {tab === 'results' && (
          <div className="space-y-8">
            <section>
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-2">
                Class progress
              </h2>
              {resultsFormula && (
                <p className="text-xs text-gray-600 mb-3">{resultsFormula}</p>
              )}
              <p className="text-xs text-gray-500 mb-4">
                {judgeAssignedCount} judge{judgeAssignedCount === 1 ? '' : 's'} assigned
              </p>
              {classProgress.length === 0 ? (
                <p className="text-gray-500 text-sm mb-6">No classes yet.</p>
              ) : (
                <ul className="space-y-2 mb-8">
                  {classProgress.map((cp) => (
                    <li
                      key={cp.class_id}
                      className={`rounded-xl border px-4 py-3 text-sm ${
                        cp.ready_for_results
                          ? 'border-emerald-800/40 bg-emerald-950/20'
                          : 'border-white/[0.06] bg-[#0c0c0c]/50'
                      }`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-medium text-white">{cp.name}</span>
                        <span className="text-xs text-gray-500">
                          {cp.scoring_complete_count}/{cp.vehicle_count} complete
                          {cp.ready_for_results ? ' · Ready' : ` · ${cp.remaining} remaining`}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
                Scoring results (per class)
              </h2>
              {results.length === 0 ? (
                <p className="text-gray-500 text-sm">No finalized scores yet.</p>
              ) : (
                <div className="space-y-8">
                  {resultsByClass.map((group) => {
                    const progress =
                      group.classId != null
                        ? classProgress.find((c) => c.class_id === group.classId)
                        : null;
                    return (
                      <div key={group.name + String(group.classId)}>
                        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                          <h3 className="font-semibold text-white">{group.name}</h3>
                          {progress && !progress.ready_for_results && (
                            <span className="text-xs text-amber-400/90">
                              Incomplete: {progress.remaining} left
                            </span>
                          )}
                        </div>
                        <ul className="space-y-3">
                          {group.items.map((r, i) => (
                            <li
                              key={r.registration_id}
                              className="card p-4"
                            >
                              <div className="flex flex-wrap items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="font-medium text-white">
                                    {i + 1}. {r.vehicle_label}
                                    {r.is_tied && (
                                      <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-300 border border-amber-700/50 rounded px-1.5 py-0.5">
                                        Tie
                                      </span>
                                    )}
                                  </p>
                                  <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-400">
                                    <span>
                                      Avg{' '}
                                      <span className="text-white font-semibold tabular-nums">
                                        {r.overall_average ?? '-'}
                                      </span>
                                    </span>
                                    <span>
                                      {r.judge_count} finalized judge
                                      {r.judge_count === 1 ? '' : 's'}
                                    </span>
                                    {r.scoring_complete === false && (
                                      <span className="text-amber-400/80">Incomplete</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {r.judge_count > 0 && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="min-h-[44px] text-gray-400"
                                      loading={publishBusy}
                                      onClick={() =>
                                        handleUnlockScores(r.registration_id, r.vehicle_label)
                                      }
                                    >
                                      Unlock scores
                                    </Button>
                                  )}
                                  {i < 3 && r.overall_average !== null && (
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      className="min-h-[44px]"
                                      loading={publishBusy}
                                      onClick={() => handlePublishTopAward(r, PLACEMENTS[i])}
                                    >
                                      Publish {i === 0 ? '1st' : i === 1 ? '2nd' : '3rd'}
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
            <EventBadgesPanel eventId={eventId} />
            <section>
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
                Published awards
              </h2>
              {awards.filter((a) => a.is_published).length === 0 ? (
                <p className="text-gray-500 text-sm">
                  No awards published yet. Prefer per-class top 3. Awards attach permanently to
                  vehicle profiles.
                </p>
              ) : (
                <ul className="space-y-2">
                  {awards
                    .filter((a) => a.is_published)
                    .map((a) => (
                      <li
                        key={a.id}
                        className="card px-4 py-3.5 text-sm"
                      >
                        <span className="font-medium text-white">{a.title}</span>
                        {a.placement && (
                          <span className="text-gray-500 ml-2">({a.placement})</span>
                        )}
                      </li>
                    ))}
                </ul>
              )}
            </section>
          </div>
        )}
    </div>
  );
};

const JudgeClassEditor: React.FC<{
  classes: EventClass[];
  initialIds: number[];
  busy: boolean;
  onSave: (ids: number[]) => void;
  onCancel: () => void;
}> = ({ classes, initialIds, busy, onSave, onCancel }) => {
  const [ids, setIds] = useState<number[]>(initialIds);
  return (
    <div className="border-t border-white/[0.06] pt-3 space-y-2">
      <p className="text-xs text-gray-500">Empty = all classes</p>
      <div className="flex flex-wrap gap-2">
        {classes.map((c) => (
          <label
            key={c.id}
            className={ids.includes(c.id) ? 'filter-chip-active gap-2' : 'filter-chip-inactive gap-2'}
          >
            <input
              type="checkbox"
              checked={ids.includes(c.id)}
              onChange={() =>
                setIds((prev) =>
                  prev.includes(c.id) ? prev.filter((x) => x !== c.id) : [...prev, c.id]
                )
              }
              className="checkbox-control"
            />
            {c.name}
          </label>
        ))}
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="primary" loading={busy} onClick={() => onSave(ids)}>
          Save classes
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default EventAdmin;
